// src/components/CourseTechniquePage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ProductDrop from "./../main/product_drop";
import "./technique.css";

/* ===================== Media query helper ===================== */
function useMediaQuery(q) {
  const [ok, setOk] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(q).matches
      : false
  );
  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(q);
    const onChange = (e) => setOk(e.matches);
    try { mql.addEventListener("change", onChange); }
    catch { mql.addListener(onChange); }
    return () => {
      try { mql.removeEventListener("change", onChange); }
      catch { mql.removeListener(onChange); } // legacy cleanup
    };
  }, [q]);
  return ok;
}

/* ===================== Accordions: delegated toggle ===================== */
function useRestoreAccordions() {
  useEffect(() => {
    const root = document.querySelector(".acc-stack");
    if (!root) return;

    // Initial sync for each accordion
    const accs = Array.from(root.querySelectorAll(".acc"));
    accs.forEach((acc) => {
      const head = acc.querySelector(".acc-head");
      const body = acc.querySelector(".acc-body");
      if (!head || !body) return;
      const open = acc.classList.contains("is-open");
      head.setAttribute("aria-expanded", open ? "true" : "false");
      body.hidden = !open;
      const icon = head.querySelector(".acc-icon");
      if (icon) icon.textContent = open ? "−" : "+";
    });

    // Delegated click handler
    const onClick = (e) => {
      const head = e.target.closest(".acc-head");
      if (!head || !root.contains(head)) return;
      const acc = head.closest(".acc");
      const body = acc?.querySelector(".acc-body");
      if (!acc || !body) return;

      const nowOpen = acc.classList.toggle("is-open");
      head.setAttribute("aria-expanded", nowOpen ? "true" : "false");
      body.hidden = !nowOpen;
      const icon = head.querySelector(".acc-icon");
      if (icon) icon.textContent = nowOpen ? "−" : "+";
    };

    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, []);
}

/* ===================== Pricing / Labels ===================== */
const PRICE_SINGLE = { nano: 3400, nanocombo: 4000, ombre: 2900, lip: 3200, lash: 1900 };
const PRICE_COMBO = {
  "nano+ombre": 5900,        // vs 6300, save 400
  "ombre+lip": 5600,         // vs 6100, save 500
  "nano+lip": 6100,          // vs 6600, save 500
  "nanocombo+lip": 6700,     // vs 7200, save 500
  "nano+ombre+lip": 8500,    // vs 9500, save 1000
};
const DEPOSIT_SINGLE = { nano: 600, nanocombo: 600, ombre: 600, lip: 600, lash: 600 };
const DEPOSIT_COMBO = {
  "nano+ombre": 950,
  "ombre+lip": 950,
  "nano+lip": 950,
  "nanocombo+lip": 950,
  "nano+ombre+lip": 1250,
};
const DURATIONS = { nano: 3, nanocombo: 3, ombre: 2, lip: 2, lash: 1 };
const LABELS = {
  nano: "Full Nano Brows",
  nanocombo: "Nano Combo Brows",
  ombre: "Ombré Brows",
  lip: "Lip Blush",
  lash: "Lash Enhancement Tattoo",
};
const ORDER = ["nano", "nanocombo", "ombre", "lip"]; // lash excluded from fundamentals combo matrix
const CLASSIC_FUNDAMENTALS = ["nano", "ombre", "lip"];
const LIP_ADDON_ORDER = ["ombre", "nano", "nanocombo"];
const canonicalJoin = (arr) => ORDER.filter((k) => arr.includes(k)).join("+");

/* ===================== Technique metadata ===================== */
const CDN = "https://cosacademyassets.s3.us-east-2.amazonaws.com";
const IMG = (name) => ({ webp: `${CDN}/${name}.webp`, png: `${CDN}/${name}.PNG` });

const TECH_META = {
  nano:  { title: "Full Nano Brows",     images: [IMG("fnwork-sample5"), IMG("fnworksample3"), IMG("fnworksample4")], liveModelDay: 3 },
  nanocombo: { title: "Nano Combo Brows", images: [IMG("fncombo"), IMG("fnworksample1"), IMG("fnwork-sample5")], liveModelDay: 2 },
  ombre: { title: "Powder Ombré Brows",  images: [IMG("fnworksample1"), IMG("fnworksample2"), IMG("fnworksample1")],  liveModelDay: 2 },
  lip:   { title: "WaterColor Lips",      images: [IMG("acadlips1"), IMG("acadlips6"), IMG("acadlips3")],            liveModelDay: 2 },
  // lash handled by poster-only layout
};

/* ===================== Overview Copy (fundamentals) ===================== */
const OVERVIEW = {
  ombre: {
    intro: "Ombré brows are a staple in the PMU industry—suitable for all skin types and easy to create. This technique delivers a soft, powdery finish that mimics makeup and heals beautifully across all client profiles.",
    who: "Total beginners, makeup artists, or anyone entering beauty for the first time",
    why: "Fast to learn, easy to master, and builds confidence with consistent results",
    income: "$100–$300/hour, with many artists earning over $100K/year",
    footer: "If you’re starting out or scaling fast, this is the service that builds your foundation and your income. It’s the ritualized entry point for artists who want speed, versatility, and high-volume bookings.",
  },
  nano: {
    intro: "Nano brows are a transformative experience for the client and a precision-based art form for the artist. Using ultra-fine strokes, this technique mimics real hair with hyper-realistic detail and long-term retention.",
    who: "Artists, beauty enthusiasts, makeup artists, and PMU professionals who love patience, control, and realism—and want to upgrade their skills with COSMETINK’s hyper-realistic nano technique",
    why: "Builds mastery in precision, artistic execution, and client transformation",
    income: "Highest in the industry, with premium pricing and elite clientele",
    footer: "If you’re drawn to detail, perfection, and high-ticket services, nano brows are your signature path. This is the service that positions you as a top-tier artist with a premium brand.",
  },
  nanocombo: {
    intro: "Nano combo brows blend fine nano hair strokes through the front of the brow into soft ombré shading from the middle through the tails. The result is airy and natural at the bulb with polished structure, definition, and lasting color where clients need it most.",
    who: "Beginners and brow artists who want one signature service that combines natural hair-stroke detail with the dependable healed results of machine shading",
    why: "Teaches you how to balance realism and structure: front hair strokes, smooth transitions, soft pixels, and a finished brow that photographs beautifully",
    income: "A premium brow service with strong demand from clients who want natural texture without giving up definition",
    footer: "Nano combo brows are ideal when you want the beauty of hair strokes and the reliability of ombré shading in one client-friendly service.",
  },
  lip: {
    intro: "Lip blushing is a fun, creative service that clients love—and nearly everyone is a candidate. It enhances natural lip tone, shape, and symmetry with a sheer wash of color and minimal trauma.",
    who: "Beginners drawn to color work, makeup artists who love soft glam, or PMU artists expanding their offerings",
    why: "Quick to learn, easy to add to your menu, and delivers instant visual transformation",
    income: "Strong standalone or add-on service with high repeat demand",
    footer: "Lip blushing is the perfect blend of artistry and accessibility—whether you’re building your PMU menu or creating a signature look clients come back for.",
  },
};

/* ===================== Helpers ===================== */
function ensureArrayCourses(input) {
  if (Array.isArray(input)) return input;
  if (input && typeof input === "object") return Object.values(input);
  return [];
}
function normalizeKeyFromCourseType(ct = "") {
  const t = String(ct || "").toLowerCase().replace(/[\s_-]+/g, "");
  if (t.includes("lash")) return "lash-enhancement";
  if (t.includes("nanocombo") && t.includes("lip")) return "nanocombo+lip";
  if (t.includes("nanocombo")) return "nanocombo";
  const parts = [];
  if (t.includes("nano")) parts.push("nano");
  if (t.includes("ombr")) parts.push("ombre");
  if (t.includes("lip")) parts.push("lip");
  return canonicalJoin(parts);
}
function normalizeKeyFromName(name = "") {
  const s = String(name || "").toLowerCase();
  const compact = s.replace(/[\s_-]+/g, "");
  if (s.includes("lash")) return "lash-enhancement";
  if (compact.includes("nanocombo") && compact.includes("lip")) return "nanocombo+lip";
  if (compact.includes("nanocombo")) return "nanocombo";
  const parts = [];
  if (s.includes("nano")) parts.push("nano");
  if (s.includes("ombr")) parts.push("ombre");
  if (s.includes("lip")) parts.push("lip");
  return canonicalJoin(parts);
}
function idFor(item) {
  return (
    (item?._id && (item._id.$oid || item._id.toString?.() || item._id)) ||
    item?.appointmentType ||
    `${item?.courseType || "course"}-${item?.dates || Math.random()}`
  );
}
const sumSingles = (arr) => arr.reduce((s, k) => s + (PRICE_SINGLE[k] || 0), 0);
function comboPrice(selection) {
  const k = canonicalJoin(selection);
  return PRICE_COMBO[k] ?? sumSingles(selection);
}
function depositForSelection(selection) {
  const k = canonicalJoin(selection);
  if (!k) return 0;
  return DEPOSIT_COMBO[k] ?? DEPOSIT_SINGLE[selection[0]] ?? 0;
}
function savings(selection) {
  const base = sumSingles(selection);
  const combo = comboPrice(selection);
  return Math.max(0, base - combo);
}

/* Full month name from label */
const MONTHS = [
  "JANUARY","FEBRUARY","MARCH","APRIL","MAY","JUNE",
  "JULY","AUGUST","SEPTEMBER","OCTOBER","NOVEMBER","DECEMBER"
];
function monthNameFrom(label) {
  const m = String(label || "").match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)/i);
  if (!m) return (label || "").split(" ")[0]?.toUpperCase() || "";
  const idx = ["jan","feb","mar","apr","may","jun","jul","aug","sep","sept","oct","nov","dec"].indexOf(m[0].toLowerCase());
  const normalized = idx === 9 ? 8 : idx; // "sept" -> "sep"
  return MONTHS[normalized] || m[0].toUpperCase();
}

/* ===================== Group courses by exact key ===================== */
function useByKey(courses) {
  return useMemo(() => {
    const byKey = {};
    ensureArrayCourses(courses).forEach((raw) => {
      if (!raw || raw.sold === true) return;
      const normalized =
        normalizeKeyFromCourseType(raw.courseType) || normalizeKeyFromName(raw.name);
      if (!normalized) return;
      const id = idFor(raw);
      const item = { ...raw, _id: id, key: normalized };
      (byKey[normalized] ||= []).push(item);
    });
    Object.values(byKey).forEach((arr) =>
      arr.sort((a, b) => String(a.dates).localeCompare(String(b.dates)))
    );
    return byKey;
  }, [courses]);
}

/* ===================== Small UI bits ===================== */
function DaysBubble({ days }) {
  return (
    <div className="days-chip" aria-label={`${days} day course`}>
      <span className="days-ring"><span className="days-core"><span className="days-num">{days}</span></span></span>
      <span className="days-word">Day{days>1?`s`:null}</span>
    </div>
  );
}

function CertificationBlock() {
  return (
    <div className="certify gradient-stroke glass">
      <h3 className="cert-title">HOW TO BECOME CERTIFIED</h3>
      <ul className="cert-list" role="list">
        <li className="cert-item">
          <span className="check">✓</span>
          <span>
            Complete Prep Work 2 weeks prior to class & obtain a{" "}
            <strong>Bloodborne Pathogen Training for Body Art</strong> Certificate (ProTrainings)
          </span>
        </li>
        <li className="cert-item">
          <span className="check">✓</span>
          <span>
            Attend the <strong>In-Person Training</strong> at COSMETINK Academy
          </span>
        </li>
        <li className="cert-item">
          <span className="check">✓</span>
          <span>
            Satisfactorily complete <strong>Live Model</strong> with supervision (in class)
          </span>
        </li>
      </ul>
    </div>
  );
}

function ConfidenceBlock() {
  return (
    <div className="confidence gradient-stroke glass">
      <div className="conf-left">
        <h3 className="conf-title">HEAR FROM A STUDENT</h3>
        <p className="conf-copy">
          Click on the video to watch our students in action—see how this class
          gave them the confidence to do their best work.
        </p>
      </div>
      <a
        className="conf-video"
        href="https://cosacademyvideos.s3.us-east-2.amazonaws.com/confidentvideo.mp4"
        aria-label="Watch our students in action"
      >
        <picture>
          <source
            className="video-thumb"
            srcSet="https://cosacademyassets.s3.us-east-2.amazonaws.com/confidence.webp"
            type="image/webp"
          />
          <img
            className="video-thumb"
            src="https://cosacademyassets.s3.us-east-2.amazonaws.com/confidence.jpg"
            alt="Students gaining confidence during live model work"
            loading="lazy"
          />
        </picture>
        <div className="play-button" aria-hidden="true">
          <i className="fa-duotone fa-play" />
        </div>
      </a>
    </div>
  );
}

function DetailsPanel({ tech }) {
  const title = LABELS[tech] || "Technique";
  const cost = PRICE_SINGLE[tech] || 0;
  const isNanoCombo = tech === "nanocombo";
  return (
    <div className="details-panel gradient-stroke glass">
      <div className="paragraph-title">CLASS DETAILS</div>

      <div className="class-detail-title">Total Cost</div>
      <div className="class-cost">
        ${cost.toLocaleString()} (includes full kit ~ $900 value)
        <br />
        <Link className="regular" to="/financing">Want to explore our financing options?</Link>
      </div>

      <div className="class-detail-title">Registration Cost</div>
      <div className="class-cost">
        $600 non-refundable deposit
        <br />
        Remaining balance due one week before course dates
      </div>

      <div className="class-detail-title">Information</div>
      <div className="class-cost">
        {isNanoCombo ? (
          <>
            In this focused 3-day training, you’ll learn COSMETINK®’s Signature {title}
            technique through theory, latex practice, supervised ombré model work on
            Day 2, and a specialty Nano Brow session on Day 3. Day 3 is a half-day
            morning class from 10:00am to 1:00pm focused on front hair strokes on
            skin, with practice on latex.{" "}
            <strong>
              This is not the Full Nano Brow technique, but this will set you up for
              success in creating Ombré Brows with hair strokes in the front.
            </strong>
          </>
        ) : (
          <>
            In this rigorous {DURATIONS[tech]}-day course, you’ll learn COSMETINK®’s
            Signature {title} technique through theory, latex practice, and hands-on
            mastery with a live model.
          </>
        )}
        <br /><br />
        Students receive a professional kit including our INKBRUSH™ machine,
        needle cartridges, mapping supplies, and pigments.
      </div>
    </div>
  );
}

function KitBlock({ tech }) {
  const KIT_CONTENT = {
    lip: {
      exclusive: [
        "INKBRUSH™ machine ($399 value)",
        "1 box of our DETAILED DEFINITION™ Needles ($39 value)",
        "Lip mapping supplies (mapping pencils & paste, mapping pen, concealer brush)",
      ],
      others:
        "ink cups, practice latex with pre-drawn lip outlines and stroke patterns, lip wands, numbing cream, machine grip tape, clip cord sleeve covers, machine power supply, and full set of lip pigments.",
    },
    nano: {
      exclusive: [
        "INKBRUSH™ machine ($399 value)",
        "1 box of our DETAILED DEFINITION™ Needles ($39 value)",
        "PERFECT BROWS™ Mapping Stickers",
        "Brow mapping supplies (including our PERFECT BROWS™ mapping string, mapping pencils, concealer brush)",
      ],
      others:
        "brow razors, ink cups, practice latex with pre-drawn brow outlines and stroke patterns, lip wands, numbing cream, machine grip tape, clip cord sleeve covers, machine power supply, and full set of eyebrow pigments.",
    },
    nanocombo: {
      exclusive: [
        "INKBRUSH™ machine ($399 value)",
        "1 box of our DETAILED DEFINITION™ Needles ($39 value)",
        "1 box of our POWDERY PIXEL™ Needles ($32 value)",
        "PERFECT BROWS™ Mapping Stickers",
        "Brow mapping supplies (including our PERFECT BROWS™ mapping string, mapping pencils, concealer brush)",
      ],
      others:
        "brow razors, ink cups, practice latex with pre-drawn brow outlines, hair-stroke patterns, and shading drills, lip wands, numbing cream, machine grip tape, clip cord sleeve covers, machine power supply, and full set of eyebrow pigments.",
    },
    ombre: {
      exclusive: [
        "INKBRUSH™ machine ($399 value)",
        "1 box of our POWDERY PIXEL™ Needles ($32 value)",
        "PERFECT BROWS™ Mapping Stickers",
        "Brow mapping supplies (including our PERFECT BROWS™ mapping string, mapping pencils, concealer brush)",
      ],
      others:
        "brow razors, ink cups, practice latex with pre-drawn brow outlines and stroke patterns, lip wands, numbing cream, machine grip tape, clip cord sleeve covers, machine power supply, and full set of eyebrow pigments.",
    },
    // Lash: per spec (no machine included; 15% off machine)
    lash: {
      exclusive: [
        "1 box of our POWDERY PIXEL™ Needles ($32 value)",
        "15% off the purchase of COSMETINK's INKBRUSH™ machine",
      ],
      others:
        "ink cups, practice latex with pre-drawn lash outlines, eyeliner pigments, cotton rounds, numbing gel, machine grip tape, clip cord sleeve covers, and other necessary lash enhancement supplies.",
    },
  };
  const kit = KIT_CONTENT[tech] || KIT_CONTENT.nano;

  return (
    <div className="kit gradient-stroke glass">
      <div className="paragraph-title">FULL KIT INCLUDED</div>

      <div className="class-detail-title">What's In My Kit?</div>
      <div className="class-cost">
        Students receive a professional kit of the highest-quality permanent
        makeup products{tech === "lash" ? "" : ", including our revolutionary COSMETINK® INKBRUSH™ machine"}
        . Your kit includes all supplies needed to perform our signature
        {tech === "lash" ? " lash enhancement " : " "}
        techniques and get started in your new career.
        <br /><br />
        Your kit is valued at <strong>$900</strong> and includes the following
        EXCLUSIVE COSMETINK® supplies:
        <ul className="kit-ul">
          {kit.exclusive.map((line, i) => (
            <li key={i}>○ {line}</li>
          ))}
        </ul>
        <div style={{ height: 8 }} />
        other supplies includes:
        <br />
        {kit.others}
      </div>

      <div className="kit-showcase">
        <ProductDrop />
      </div>
    </div>
  );
}

function CurriculumBlock({ tech }) {
  const CURRICULUM_MAP = {
    nano: [
      "COSMETINK’s Signature Full Nano Brows Technique — 5 Hours of At-Home Pre-Course Online Work + 3 Days In-Person, Hands-on instruction",
      "Option to participate in apprenticeship program: complete 5 live models in our facility within 6 months of course completion (earn a 100hr Certification)",
      "Full Introductory Kit ... INKBRUSH™ PMU Machine and Needles",
      "Social Media in the Industry",
      "Modern Marketing, Pricing Strategies, How to Attract Clients",
      "Skin Theory & Depth for Permanent Makeup",
      "Color Theory & Proper Pigmentation",
      "The Science of Beauty: Brow Mapping, Shaping, & Nano Hair-Stroke Design",
      "Overview of Tools and Supplies",
      "Proper Sanitation & Bloodborne Pathogen Training",
      "Machine Fundamentals for Crisp Nano Strokes (Speed, Voltage, Hand Position)",
      "Stroke Patterns, Flow, and Density Planning",
      "Demo Work + Hands-On Latex Practice with Individualized Guidance",
      "Live Model (supervised)",
      "Certification as a COSMETINK® PRO Artist",
    ],
    nanocombo: [
      "COSMETINK’s Signature Nano Combo Brows Technique — 5 Hours of At-Home Pre-Course Online Work + 3 Days In-Person, Hands-on instruction",
      "Option to participate in apprenticeship program: complete 5 live models in our facility within 6 months of course completion (earn a 100hr Certification)",
      "Full Introductory Kit ... INKBRUSH™ PMU Machine and Nano + Powder Brow Needles",
      "Social Media in the Industry",
      "Modern Marketing, Pricing Strategies, How to Attract Clients",
      "Skin Theory & Depth for Permanent Makeup",
      "Color Theory & Proper Pigmentation",
      "The Science of Beauty: Brow Mapping, Shaping, & Combo Brow Design",
      "Nano Front Hair-Stroke Patterns, Flow, and Density Planning",
      "Ombré Shading from Middle to Tail",
      "Transition Work: Blending Hair Strokes into Soft Pixelation",
      "Demo Work + Hands-On Latex Practice with Individualized Guidance",
      "Live Ombré Model (supervised) on Day 2",
      "Specialty Day 3 Half-Day Morning Class (10:00am - 1:00pm): Nano Brow Front Hair Strokes on Skin",
      "Certification as a COSMETINK® PRO Artist",
    ],
    ombre: [
      "COSMETINK’s Signature Powder Ombré Brows Technique — 5 Hours at home + 2 Days in-person",
      "Apprenticeship option (5 live models / 6 months for 100hr Cert)",
      "Full Introductory Kit ... INKBRUSH™ PMU Machine and Needles",
      "Social Media in the Industry",
      "Modern Marketing & Pricing",
      "Skin Theory & Depth",
      "Color Theory & Pigmentation",
      "Brow Mapping & Ombré Gradient Theory",
      "Sanitation & Bloodborne Pathogen Training",
      "Machine Technique: Soft Powdery Pixelation",
      "Beautiful Gradients & Definition",
      "Demo + Latex Practice",
      "Live Model (supervised)",
      "COSMETINK® PRO Certification",
    ],
    lip: [
      "COSMETINK’s Signature Watercolor Lips — 5 Hours at home + 2 Days in-person",
      "Apprenticeship option (5 live models / 6 months for 100hr Cert)",
      "Full Introductory Kit ... INKBRUSH™ PMU Machine and Needles",
      "Social Media in the Industry",
      "Marketing & Pricing",
      "Skin Theory & Depth",
      "Color Theory (Lip-safe, undertones, neutralization)",
      "Mapping, Shaping & Border Softening",
      "Machine Technique: Watercolor Lip Saturation & Blending",
      "Demo + Latex Practice",
      "Live Model (supervised)",
      "COSMETINK® PRO Certification",
    ],
    lash: [
      "COSMETINK’s Signature Lash Enhancement Technique — 1 Day In-Person Mentorship",
      "Skin, Color & Depth Considerations for Lash Line Work",
      "Safety, Sanitation & Bloodborne Pathogen Training",
      "Machine Fundamentals (Speed, Voltage, Needle Choice)",
      "Lash Line Mapping & Wing Planning",
      "Demo + Latex Practice",
      "Live Model (supervised) and Certification Requirements",
    ],
  };
  const lines = CURRICULUM_MAP[tech] || CURRICULUM_MAP.nano;
  return (
    <div className="curriculum">
      <h3 className="curr-title">CLASS CURRICULUM</h3>
      <ol className="curr-list">
        {lines.map((line, i) => (
          <li key={i} className="curr-row">
            <span className="dot" aria-hidden="true" />
            <span className="curr-text">{line}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ImportantDetailsBlock({ tech }) {
  const isLash = tech === "lash";
  const isNanoCombo = tech === "nanocombo";
  return (
    <div className="important gradient-stroke glass">
      <h3 className="imp-title">Important Details</h3>

      {isLash ? (
        <>
          <p className="imp-p">
            This is a <strong>1:1 mentorship</strong>. You will choose your own date and we’ll confirm availability.
          </p>
          <p className="imp-p">
            Registration is a <strong>$600 non-refundable deposit</strong>. The remaining balance is due one week prior to training.
          </p>
          <p className="imp-p">
            <em>
              * You are responsible for providing a live model (18+, no previous lash-line PMU).
            </em>
          </p>
        </>
      ) : isNanoCombo ? (
        <>
          <p className="imp-p">
            This course consists of an at-home <strong>and</strong> in-person component.
            At-home preparation work will be sent two weeks prior to the course dates,
            and will require approximately <strong>15 hours</strong> of study.
          </p>
          <p className="imp-p">
            Live Model Day is your <strong>Ombré Brows model on Day 2</strong>.
            Students are responsible for providing their model (<strong>18+</strong>,
            no previous brow PMU). Day 3 is a{" "}
            <strong>half-day morning specialty Nano Brow class from 10:00am to 1:00pm</strong>{" "}
            focused on front hair strokes on skin, with practice on latex.{" "}
            <strong>
              This is not the Full Nano Brow technique, but this will set you up for
              success in creating Ombré Brows with hair strokes in the front.
            </strong>
          </p>
        </>
      ) : (
        <>
          <p className="imp-p">
            This course consists of an at-home <strong>and</strong> in-person component.
            At-home preparation work will be sent two weeks prior to the course dates, and
            will require approximately <strong>15 hours</strong> of study. Please block
            out time to complete the assignments and quizzes so we can jump <strong>right</strong> into hands-on work.
          </p>
          <p className="imp-p">
            Class times are typically <strong>10:00am – 7:00pm</strong> (ending times may vary).
            Be prepared to practice at home after your first class day to perform your best work
            on your live model the following day.
          </p>
          <p className="imp-p">
            <em>
              * You are responsible for providing a model for your live model work. Models
              must not have had previous PMU on the focus area, must be patient and comfortable,
              and must be <strong>18+</strong>.
            </em>
          </p>
        </>
      )}
    </div>
  );
}

/* ===================== Booking Panel (fundamentals only) ===================== */
function BookingPanel({
  baseTech,
  selected,
  setSelected,
  exactOptions,
  activeId,
  setActiveId,
  inlineOptionsBase,
  inlineActiveId,
}) {
  const selectionKey = canonicalJoin(selected);
  const isBaseSelection = selectionKey === baseTech;
  const listForBooking = (exactOptions && exactOptions.length)
    ? exactOptions
    : (isBaseSelection ? (inlineOptionsBase || []) : []);

  const initialLocalId =
    (listForBooking.find(o => o?._id === activeId)?._id) ||
    (listForBooking.find(o => o?._id === inlineActiveId)?._id) ||
    (listForBooking[0]?._id) ||
    null;

  const [bookActiveId, setBookActiveId] = useState(initialLocalId);
  useEffect(() => {
    const nextId =
      (listForBooking.find(o => o?._id === bookActiveId)?._id) ||
      (listForBooking.find(o => o?._id === activeId)?._id) ||
      (listForBooking.find(o => o?._id === inlineActiveId)?._id) ||
      (listForBooking[0]?._id) ||
      null;
    if (nextId !== bookActiveId) setBookActiveId(nextId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listForBooking, activeId, inlineActiveId]);

  const title = ORDER.filter((k) => selected.includes(k)).map((k) => LABELS[k]).join(" + ");
  const total = comboPrice(selected);
  const deposit = depositForSelection(selected);
  const save = savings(selected);
  const singlesSum = sumSingles(selected);

  const activeLocal = listForBooking.find((o) => o._id === bookActiveId) || null;
  const canBook = !!activeLocal?.appointmentType;
  const acuityHref = canBook
    ? `https://app.acuityscheduling.com/schedule.php?owner=17444389&appointmentType=${encodeURIComponent(
        activeLocal.appointmentType
      )}`
    : "#";

  function priceIf(selection) { return comboPrice(selection); }
  function priceDiffIfAdded(k) {
    const next = ORDER.filter((key) => [...selected, k].includes(key));
    return priceIf(next) - priceIf(selected);
  }
  function priceDiffIfRemoved(k) {
    const next = selected.filter((x) => x !== k);
    return priceIf(selected) - priceIf(next);
  }

  function isDisabledSlot(k, selection = selected) {
    if (baseTech !== "lip" || selection.includes(k)) return false;
    const hasOtherBrow = selection.includes("nano") || selection.includes("ombre");
    const hasNanoCombo = selection.includes("nanocombo");
    return (k === "nanocombo" && hasOtherBrow) || ((k === "nano" || k === "ombre") && hasNanoCombo);
  }

  function onToggle(k) {
    if (k === baseTech) return;
    if (isDisabledSlot(k)) return;
    setSelected((prev) =>
      prev.includes(k)
        ? (prev.filter((x) => x !== k).length ? prev.filter((x) => x !== k) : [baseTech])
        : ORDER.filter((key) => [...prev, k].includes(key))
    );
  }
  const slots = baseTech === "lip"
    ? LIP_ADDON_ORDER
    : CLASSIC_FUNDAMENTALS.filter((k) => k !== baseTech);
  const isNanoComboBase = baseTech === "nanocombo";
  const isFullBrowSelection =
    selected.includes("nano") && selected.includes("ombre") && !selected.includes("nanocombo");
  const hasLip = selected.includes("lip");
  const fullBrowNext = isFullBrowSelection
    ? (hasLip ? ["nanocombo", "lip"] : ["nanocombo"])
    : (hasLip ? ["nano", "ombre", "lip"] : ["nano", "ombre"]);
  const lipNext = hasLip
    ? (isFullBrowSelection ? ["nano", "ombre"] : ["nanocombo"])
    : (isFullBrowSelection ? ["nano", "ombre", "lip"] : ["nanocombo", "lip"]);
  const fullBrowDiff = comboPrice(fullBrowNext) - comboPrice(selected);
  const lipDiff = comboPrice(lipNext) - comboPrice(selected);

  return (
    <fieldset className="bookbox gradient-outline" style={{ borderRadius: 18, marginTop: 14 }}>
      <legend className="booklegend">
        <span className="booklegend-chip">Book Your Course</span>
      </legend>

      <div className="bookbox-inner">
        {/* Title + total days */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ fontWeight: 900, fontSize: "clamp(16px,1.8vw,20px)" }}>
            {title || LABELS[baseTech]}
          </div>
          <div className="days-chip" aria-label="total course days">
            <span className="days-ring"><span className="days-core"><span className="days-num">
              {ORDER.filter((k) => selected.includes(k)).reduce((s, k) => s + DURATIONS[k], 0)}
            </span></span></span>
            <span className="days-word">Days</span>
          </div>
        </div>

        {/* Stable add/remove slots */}
        {isNanoComboBase ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 10 }}>
            <button
              type="button"
              className="combo-chip"
              onClick={() => setSelected(fullBrowNext)}
              title="Take the full Nano and Ombré brow course"
              style={isFullBrowSelection ? { opacity: 0.85, background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.18)" } : undefined}
            >
              {isFullBrowSelection ? "Use Nano Combo Brows" : "Take Full Nano Brows + Ombré Brow Course"}
              <span style={{ fontWeight: 900, opacity: 0.96 }}>
                &nbsp;({fullBrowDiff < 0 ? "−" : "+"}${Math.abs(fullBrowDiff).toLocaleString()})
              </span>
            </button>
            <button
              type="button"
              className="combo-chip"
              onClick={() => setSelected(lipNext)}
              title={`${hasLip ? "Remove" : "Add"} Lip Blush`}
              style={hasLip ? { opacity: 0.85, background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.18)" } : undefined}
            >
              {hasLip ? "Remove " : "Add "}Lip Blush
              <span style={{ fontWeight: 900, opacity: 0.96 }}>
                &nbsp;({lipDiff < 0 ? "−" : "+"}${Math.abs(lipDiff).toLocaleString()})
              </span>
            </button>
          </div>
        ) : slots.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 10 }}>
            {slots.map((k) => {
              const isOn = selected.includes(k);
              const isDisabled = isDisabledSlot(k);
              const label = LABELS[k];
              const diff = isOn ? priceDiffIfRemoved(k) : priceDiffIfAdded(k);
              const chipStyle = isDisabled
                ? {
                    opacity: 0.38,
                    filter: "grayscale(1)",
                    cursor: "not-allowed",
                    background: "rgba(255,255,255,.04)",
                    borderColor: "rgba(255,255,255,.12)",
                  }
                : isOn
                  ? { opacity: 0.85, background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.18)" }
                  : undefined;
              return (
                <button
                  key={k}
                  type="button"
                  className="combo-chip"
                  onClick={() => onToggle(k)}
                  title={`${isOn ? "Remove" : "Add"} ${label}`}
                  aria-disabled={isDisabled ? "true" : undefined}
                  disabled={isDisabled}
                  style={chipStyle}
                >
                  {isOn ? "Remove " : "Add "}{label}
                  <span style={{ fontWeight: 900, opacity: 0.96 }}>
                    &nbsp;({isOn ? "−" : "+"}${Math.abs(diff).toLocaleString()})
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Price summary */}
        <div className="sticky-breakdown" style={{ maxWidth: 820, marginTop: 8 }}>
          <div className="bd-row"><span>Singles total</span><span>${singlesSum.toLocaleString()}</span></div>
          {save > 0 && (<div className="bd-row save"><span>You save</span><span>−${save.toLocaleString()}</span></div>)}
          {selected.length > 1 && (<div className="bd-row strong"><span>Combo price</span><span>${total.toLocaleString()}</span></div>)}
          <div className="bd-row strong"><span>Deposit due now</span><span>${deposit.toLocaleString()}</span></div>
        </div>

        {/* Small opaque divider */}
        <div className="bookbox-divider" role="separator" aria-hidden="true" />

        {/* Dates (plain list, left) + Book (right) */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "end", marginTop: 6 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div className="bd-row red"><span>Next Dates:</span></div>

            {listForBooking.length ? listForBooking.map((o) => {
              const on = o._id === bookActiveId;
              const label = String(o.dates || "");
              const month = monthNameFrom(label);
              const rest = label.replace(/^[^\s]+\s+/, "");
              return (
                <button
                  key={o._id}
                  type="button"
                  onClick={() => setBookActiveId(o._id)}
                  aria-pressed={on}
                  title={`Choose ${o.dates}`}
                  style={{
                    textAlign: "left",
                    padding: "8px 0",
                    border: 0,
                    background: "transparent",
                    color: "#fff",
                    fontWeight: on ? 900 : 700,
                    opacity: on ? 1 : 0.92,
                    cursor: "pointer",
                  }}
                >
                  <span style={{ display: "block" }}>
                    <strong style={{ textTransform: "uppercase" }}>{month}</strong>&nbsp;{rest}
                  </span>
                  <span style={{ display: "block", opacity: 0.72 }}>{o.location}</span>
                </button>
              );
            }) : (
              <div style={{ color: "#fff", opacity: 0.8, padding: "6px 0", fontWeight: 700 }}>
                New dates coming soon
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "end" }}>
            <a
              className="tech-cta"
              href={acuityHref}
              onClick={(e) => { if (!canBook) e.preventDefault(); }}
              aria-disabled={!canBook ? "true" : undefined}
            >
              Book Now
            </a>
            <div style={{ fontSize: 12, opacity: 0.85, color: "#fff" }}>
              Total:&nbsp;<strong>${total.toLocaleString()}</strong>
              <span>&nbsp;|&nbsp;Deposit:&nbsp;<strong>${deposit.toLocaleString()}</strong></span>
              {save > 0 && (<span style={{ color: "#79ff9b", fontWeight: 900 }}>&nbsp;(Save ${save.toLocaleString()})</span>)}
            </div>
          </div>
        </div>
      </div>
    </fieldset>
  );
}

/* ===================== MAIN ===================== */
export default function CourseTechniquePage({ fetchAvailableCourses, courses }) {
  const { name } = useParams(); // /courses/:name  (nano | ombre | lips | lash-enhancement)
  const baseTech =
    name === "lips" ? "lip" :
    name === "lash-enhancement" ? "lash" :
    (name || "nano");
  const initialSelection = useMemo(() => [baseTech], [baseTech]);

  useRestoreAccordions();

  const heroRef = useRef(null);
  useMediaQuery("(min-width: 1024px)"); // layout parity only

  // Smooth scroll to top when route/tech changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [name]);

  useEffect(() => { fetchAvailableCourses?.(); }, [fetchAvailableCourses]);

  // ---- Selection state & course maps ----
  const [selected, setSelected] = useState(initialSelection);
  const byKey = useByKey(courses);

  // LASH data (poster + appointmentType)
  const lashData = useMemo(() => {
    const list = byKey["lash-enhancement"] || [];
    return list.find(Boolean) || null;
  }, [byKey]);

  const isLash = name === "lash-enhancement";

  // Poster for fundamentals (from API .image field)
  const posterSrc = useMemo(() => {
    if (isLash) return null;
    const list = byKey[baseTech] || [];
    const found = list.find(c => c?.image) || list[0];
    return found?.image || null;
  }, [byKey, baseTech, isLash]);

  // Base-tech-only options for the inline dates panel (do NOT react to combo selection)
  const inlineOptionsBase = useMemo(() => {
    if (isLash) return [];
    return (byKey[baseTech] || []).filter(
      (c) => c.location === "Bethlehem, Pennsylvania"
    );
  }, [byKey, baseTech, isLash]);

  // Exact selection options (e.g., nano+ombre)
  const exactKey = canonicalJoin(selected);
  const exactOptions = useMemo(() => {
    if (isLash) return [];
    return (byKey[exactKey] || []).filter(
      (c) => c.location === "Bethlehem, Pennsylvania"
    );
  }, [byKey, exactKey, isLash]);

  const [activeId, setActiveId] = useState(null);
  useEffect(() => {
    setSelected(initialSelection);
    setActiveId(null);
  }, [initialSelection, name]);

  useEffect(() => { if (!isLash) setActiveId(exactOptions[0]?._id ?? null); }, [exactOptions, isLash]);

  const meta = TECH_META[baseTech] || {
    title: LABELS[baseTech] || "Technique",
    images: [],
    liveModelDay: 2,
  };

  const WHAT_TITLE =
    baseTech === "nano" ? "WHAT ARE FULL NANO BROWS?"
    : baseTech === "nanocombo" ? "WHAT ARE NANO COMBO BROWS?"
    : baseTech === "ombre" ? "WHAT ARE OMBRÉ BROWS?"
    : baseTech === "lip" ? "WHAT IS LIP BLUSH?"
    : "WHAT IS LASH ENHANCEMENT?";

  return (
    <div className="tech-shell">
      {/* ===== HERO ===== */}
      {isLash ? (
        // Poster-only hero (poster left on desktop / top on mobile)
        <section className="tech-hero">
          <div className="tech-grid tech-grid--poster">
            {/* LEFT: poster only */}
            <div className="poster-only">
              <img
                className="poster-img"
                src={lashData?.image}
                alt="COSMETINK® Lash Enhancement — course poster"
                loading="eager"
              />
            </div>

            {/* RIGHT: info / booking for 1:1 */}
            <aside className="tech-info">
              <div className="tech-kicker">Advanced · 1:1 Mentorship</div>

              <div className="title-row">
                <h1 className="tech-title">
                  <span className="grad">Lash Enhancement Tattoo</span>
                </h1>
                <DaysBubble days={DURATIONS.lash} />
              </div>

              <div className="info-chips">
                <span className="apprentice-pill">Choose Your Own Date</span>
              </div>

              <p className="tech-sub">
                One-on-one mentorship. You’ll schedule the day that works for you; we’ll confirm
                availability and finalize your session details.
              </p>

              <br />
              <fieldset className="bookbox gradient-outline" style={{ borderRadius: 18, marginTop: 14 }}>
                <legend className="booklegend"><span className="booklegend-chip">Book Your 1:1</span></legend>
                <div className="bookbox-inner">
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "end", marginTop: 6 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <div className="bd-row red"><span>Choose Your Own Date:</span></div>
                      <div style={{ color: "#fff", opacity: 0.9, fontWeight: 700 }}>
                        Private training at COSMETINK® Academy (Bethlehem, PA).
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "end" }}>
                      <a
                        className="tech-cta"
                        href={`https://app.acuityscheduling.com/schedule.php?owner=17444389&appointmentType=${encodeURIComponent(
                          lashData?.appointmentType || "85510053"
                        )}`}
                      >
                        Schedule 1:1
                      </a>
                      <div style={{ fontSize: 12, opacity: 0.85, color: "#fff" }}>
                        Total:&nbsp;<strong>${PRICE_SINGLE.lash.toLocaleString()}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>
            </aside>
          </div>
        </section>
      ) : (
        // Fundamentals hero (poster + two work images + inline dates + booking panel)
        <section ref={heroRef} className="tech-hero">
          <div className="tech-grid tech-grid--60_40">
            {/* LEFT: poster + two work images */}
            <div className="tech-media poster-media">
              {/* Poster */}
              <figure className="poster-wrap" aria-label="Course poster">
                {posterSrc ? (
                  <img
                    className="poster-img"
                    src={posterSrc}
                    alt={`${LABELS[baseTech]} course poster`}
                    loading="eager"
                  />
                ) : (
                  <div className="poster-ph" />
                )}
              </figure>

              {/* Two work images */}
              <div className="work-two">
                {[0,1].map((i) => {
                  const img = meta?.images?.[i];
                  return (
                    <figure key={i} className="work-card">
                      {img ? (
                        <picture>
                          <source srcSet={img.webp} type="image/webp" />
                          <img className="work-img" src={img.png} alt={`${LABELS[baseTech]} work ${i+1}`} loading="lazy" />
                        </picture>
                      ) : (
                        <div className="work-ph" />
                      )}
                    </figure>
                  );
                })}
              </div>
            </div>

            {/* RIGHT: tech info + inline dates + booking panel */}
            <aside className="tech-info">
              <div className="tech-kicker">Fundamentals</div>

              <div className="title-row">
                <h1 className="tech-title"><span className="grad">{LABELS[baseTech]}</span></h1>
                <DaysBubble days={DURATIONS[baseTech]} />
              </div>

              <div className="info-chips">
                <span className="apprentice-pill">+ Optional Apprenticeship</span>
              </div>

              {baseTech === "nanocombo" ? (
                <p className="tech-sub">
                  Live Model Day is your <strong>Ombré Brows model on Day 2</strong>.
                  Students are responsible for providing their model (<strong>18+</strong>,
                  no previous brow PMU). Day 3 is a{" "}
                  <strong>half-day morning specialty Nano Brow class from 10:00am to 1:00pm</strong>{" "}
                  focused on front hair strokes on skin, with practice on latex.{" "}
                  <strong>
                    This is not the Full Nano Brow technique, but this will set you up for
                    success in creating Ombré Brows with hair strokes in the front.
                  </strong>
                </p>
              ) : (
                <p className="tech-sub">
                  Live Model Day is on <strong>Day {TECH_META[baseTech]?.liveModelDay ?? 2}</strong>. Students are responsible for providing their model
                  (<strong>18+</strong>, no previous PMU on target area).
                </p>
              )}

              {/* Inline Dates (locked to base technique only) */}
              <div className="dates-panel dates-panel--inline">
                <div className="dates-head">
                  <h3 className="dates-title">Upcoming Dates</h3>
                </div>
                <div className="dates-grid">
                  {inlineOptionsBase.length ? inlineOptionsBase.map((o) => {
                    const isActive = o._id === activeId;
                    const label = String(o.dates || "");
                    const month = monthNameFrom(label);
                    const rest = label.replace(/^[^\s]+\s+/, "");
                    return (
                      <button
                        key={o._id}
                        type="button"
                        className={`date-chip2 ${isActive ? "is-active" : ""}`}
                        onClick={() => setActiveId(o._id)}
                        aria-pressed={isActive}
                        title={`Choose ${o.dates}`}
                      >
                        <span className="chip-when">
                          <span style={{ display: "block", fontSize: "0.86em", opacity: 0.92 }}>{month}</span>
                          <span style={{ display: "block", marginTop: "2px" }}>{rest}</span>
                        </span>
                        <span className="chip-where">{o.location}</span>
                      </button>
                    );
                  }) : <div className="date-empty">New dates coming soon</div>}
                </div>
              </div>

              <br />

              {/* Booking Panel */}
              <BookingPanel
                key={name || baseTech}
                baseTech={baseTech}
                selected={selected}
                setSelected={setSelected}
                exactOptions={exactOptions}
                activeId={activeId}
                setActiveId={setActiveId}
                inlineOptionsBase={inlineOptionsBase}
                inlineActiveId={activeId}
              />
            </aside>
          </div>
        </section>
      )}

      {/* ===== Light Overview Q&A ===== */}
      <section className="section section--light">
        <div className="container">
          {!isLash && <div className="what-splash" aria-hidden />}
          <h2 className="what-title">{WHAT_TITLE}</h2>

          <div className="overview">
            {isLash ? (
              <>
                <p className="overview-intro">
                  Lash enhancement creates a fuller, mascara-type of effect along and within the lash line.
                  In this one-day mentorship, you’ll learn our signature approach from
                  safety to stroke execution—designed for artists who want a precise,
                  premium service with beautiful healing and retention.
                </p>
                <div className="overview-cards overview-cards--qa">
                  <div className="overview-card gradient-stroke">
                    <div className="card-label">Who is it for?</div>
                    <div className="card-text">
                      Artists with prior PMU experience who want focused, 1:1 instruction in lash-line technique.
                    </div>
                  </div>
                  <div className="overview-card gradient-stroke">
                    <div className="card-label">Why train?</div>
                    <div className="card-text">
                      Private mentorship accelerates skill acquisition and ensures safe, elegant application.
                    </div>
                  </div>
                  <div className="overview-card gradient-stroke">
                    <div className="card-label">What you’ll do</div>
                    <div className="card-text">
                      Theory, latex practice, and a supervised live model—end-to-end lash enhancement execution.
                    </div>
                  </div>
                </div>
                <p className="overview-footer">
                  You’ll leave with a refined, confident approach to lash enhancement that’s client-ready and brand-aligned.
                </p>
              </>
            ) : (
              <>
                <p className="overview-intro">{OVERVIEW[baseTech]?.intro || OVERVIEW.nano.intro}</p>
                <div className="overview-cards overview-cards--qa">
                  <div className="overview-card gradient-stroke">
                    <div className="card-label">Who is it for?</div>
                    <div className="card-text">{OVERVIEW[baseTech]?.who || OVERVIEW.nano.who}</div>
                  </div>
                  <div className="overview-card gradient-stroke">
                    <div className="card-label">Why train?</div>
                    <div className="card-text">{OVERVIEW[baseTech]?.why || OVERVIEW.nano.why}</div>
                  </div>
                  <div className="overview-card gradient-stroke">
                    <div className="card-label">Income potential</div>
                    <div className="card-text">{OVERVIEW[baseTech]?.income || OVERVIEW.nano.income}</div>
                  </div>
                </div>
                <p className="overview-footer">{OVERVIEW[baseTech]?.footer || OVERVIEW.nano.footer}</p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ===== Accordions ===== */}
      <section className="section section--dark">
        <div className="container acc-stack">
          <div className="acc is-open">
            <button type="button" className="acc-head" aria-expanded="true">
              <span className="acc-title">Class Details</span>
              <span className="acc-icon" aria-hidden>−</span>
            </button>
            <div className="acc-body">
              {isLash ? (
                <div className="details-panel gradient-stroke glass">
                  <div className="paragraph-title">CLASS DETAILS</div>

                  <div className="class-detail-title">Total Cost</div>
                  <div className="class-cost">
                    ${PRICE_SINGLE.lash.toLocaleString()} (includes kit with necessary lash enhancement supplies | does not include machine)
                  </div>

                  <div className="class-detail-title">Registration Cost</div>
                  <div className="class-cost">
                    $600 non-refundable deposit
                    <br />
                    Remaining balance due one week before course date
                  </div>

                  <div className="class-detail-title">Information</div>
                  <div className="class-cost">
                    In this rigorous 1-day course, you’ll learn COSMETINK®’s signature Lash Enhancement technique from start to finish.
                    You will complete a <strong>live model</strong> under supervision in class.
                  </div>
                </div>
              ) : (
                <DetailsPanel tech={baseTech} />
              )}
            </div>
          </div>

          <div className="acc">
            <button type="button" className="acc-head" aria-expanded="false">
              <span className="acc-title">How to Become Certified</span>
              <span className="acc-icon" aria-hidden>+</span>
            </button>
            <div className="acc-body" hidden>
              {isLash ? (
                <div className="certify gradient-stroke glass">
                  <h3 className="cert-title">HOW TO BECOME CERTIFIED</h3>
                  <ul className="cert-list" role="list">
                    <li className="cert-item"><span className="check">✓</span><span>OBTAIN / SUBMIT a <strong>Bloodborne Pathogen Training for Body Art</strong> certificate (ProTrainings)</span></li>
                    <li className="cert-item"><span className="check">✓</span><span>Attend the <strong>1-day in-person training</strong> at COSMETINK® Academy</span></li>
                    <li className="cert-item"><span className="check">✓</span><span>Satisfactorily complete a <strong>live model</strong> with supervision (in class)</span></li>
                  </ul>
                </div>
              ) : (
                <CertificationBlock />
              )}
            </div>
          </div>

          <div className="acc">
            <button type="button" className="acc-head" aria-expanded="false">
              <span className="acc-title">Hear From a Student</span>
              <span className="acc-icon" aria-hidden>+</span>
            </button>
            <div className="acc-body" hidden>
              <ConfidenceBlock />
            </div>
          </div>

          <div className="acc">
            <button type="button" className="acc-head" aria-expanded="false">
              <span className="acc-title">Full Kit Included</span>
              <span className="acc-icon" aria-hidden>+</span>
            </button>
            <div className="acc-body" hidden>
              <KitBlock tech={isLash ? "lash" : baseTech} />
            </div>
          </div>

          <div className="acc">
            <button type="button" className="acc-head" aria-expanded="false">
              <span className="acc-title">Class Curriculum</span>
              <span className="acc-icon" aria-hidden>+</span>
            </button>
            <div className="acc-body" hidden>
              <CurriculumBlock tech={isLash ? "lash" : baseTech} />
            </div>
          </div>

          <div className="acc">
            <button type="button" className="acc-head" aria-expanded="false">
              <span className="acc-title">Important Details</span>
              <span className="acc-icon" aria-hidden>+</span>
            </button>
            <div className="acc-body" hidden>
              <ImportantDetailsBlock tech={isLash ? "lash" : baseTech} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
